import React from 'react';
import { getIntl } from '../../../../../utils/intl';
import { Linking } from 'react-native';
import { constants, helpCenterUrl, scheduleSupportChatUrl } from '../../../../../utils/constants';
import { ActionButtonLayout } from '../../../../v2BaseComponents/ActionButtonLayout';
import MainContentContainer from '../../../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack, contentStackSpacing } from '../../../../v2BaseComponents/Stack';
import TitledSection from '../../../../v2BaseComponents/TitledSection';
import Text from '../../../../v2BaseComponents/Text';
import Button from '../../../../v2BaseComponents/Button';
import { AnimateLoadedContent } from '../../../../v2BaseComponents/Loader';
import LabelValueDisplay from '../../../../v2BaseComponents/LabelValueDisplay';
import { useAppType } from '../../../../../hooks/useAppType';
import { useRedirectToFeedbackAndRoadmap } from '../../../../../hooks/useRedirectToFeedbackAndRoadmap';

const intl = getIntl('myAccount.support', {
    support: 'Support',
    subHeader:
        'If you have a question or problem with your service, please contact us with the buttons below (SMS or Email)',
    sendSMS: 'Send SMS {number}',
    sendEmail: 'Send Email {email}',
    supportHours: 'Support Hours',
    mondayToFriday: 'Monday - Friday',
    workHours: '9 AM - 5 PM (ET)',
    afterHours: '1-2 hours',
    helpCenter: 'Help Center',
    logoAltText: 'Primary Record Logo',
    contactSupport: 'Contact Support',
    responseTime: 'Response time',
    feedbackAndRoadmap: 'Feedback & Roadmap',
    scheduleSupportCall: 'Schedule Support Call',
});

export type SupportScreenOwnProps = {};

export type SupportScreenProps = SupportScreenOwnProps;

const SupportScreen: React.FC<SupportScreenProps> = () => {
    const appType = useAppType();
    const { redirectToFeedbackAndRoadmap, isRedirecting } = useRedirectToFeedbackAndRoadmap();

    const { supportEmail, supportEmailBody, supportEmailSubject, supportSMS, supportSMSBody } = constants;

    const handleOpenUrl = (url: string) => {
        Linking.openURL(url).catch((err) => console.error('An error occurred opening link', err));
    };

    const mailUrl = `mailto:${supportEmail}?subject=${supportEmailSubject}&body=${supportEmailBody}`;

    return (
        <AnimateLoadedContent isLoading={isRedirecting}>
            {() => (
                <ActionButtonLayout whiteBackground scrollable>
                    <MainContentContainer>
                        <ContentStack>
                            <TitledSection title={intl('contactSupport')}>
                                <Stack vertical fullWidth space={8}>
                                    <Text variant="body-1">{intl('subHeader')}</Text>
                                    <LabelValueDisplay
                                        headerTitle={intl('supportHours')}
                                        items={[
                                            {
                                                label: intl('mondayToFriday'),
                                                value: intl('workHours'),
                                            },
                                            {
                                                label: intl('responseTime'),
                                                value: intl('afterHours'),
                                            },
                                        ]}
                                    />
                                </Stack>
                            </TitledSection>
                            <Stack vertical fullWidth space={contentStackSpacing} align="center">
                                <Button
                                    title={intl('helpCenter')}
                                    icon="Info"
                                    variant="primary"
                                    onPress={() => handleOpenUrl(helpCenterUrl)}
                                    size="lg"
                                />
                                <Button
                                    title={intl('sendEmail', { email: supportEmail })}
                                    variant="primary"
                                    icon="Mail"
                                    onPress={() => {
                                        handleOpenUrl(mailUrl);
                                    }}
                                    size="lg"
                                />
                                <Button
                                    icon="MessageSquare"
                                    title={intl('sendSMS', { number: supportSMS })}
                                    onPress={() => {
                                        handleOpenUrl(`sms:${supportSMS}?&body=${supportSMSBody}`);
                                    }}
                                    variant="primary"
                                    size="lg"
                                />
                                <Button
                                    icon="Calendar"
                                    title={intl('scheduleSupportCall')}
                                    onPress={() => {
                                        handleOpenUrl(scheduleSupportChatUrl);
                                    }}
                                    variant="primary"
                                    size="lg"
                                />
                                {appType === 'professional' ? (
                                    <>
                                        <Button
                                            title={intl('feedbackAndRoadmap')}
                                            icon="Compass"
                                            variant="primary"
                                            size="lg"
                                            onPress={redirectToFeedbackAndRoadmap}
                                        />
                                    </>
                                ) : null}
                            </Stack>
                        </ContentStack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(SupportScreen);
